/**
 * App shell and routing.
 *
 * Routes are real paths (`/g/:id`) served by the Worker's SPA fallback, rather
 * than the original's `?game=` query parameter juggled with history.replaceState.
 */

import { useCallback, useEffect, useState } from 'react';
import { signOut, useSession } from '../lib/auth-client';
import { GamePage } from './GamePage';
import { GamesLanding } from './GamesLanding';
import { AuthPanel } from './AuthPanel';
import { APP_NAME, APP_TAGLINE } from '../../shared/branding';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
}

export function App() {
  const { data: session, isPending } = useSession();
  const [route, setRoute] = useState(() => window.location.pathname);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((path: string) => {
    window.history.pushState({}, '', path);
    setRoute(path);
  }, []);

  if (isPending) {
    return <div className="app empty-state">Loading…</div>;
  }

  /*
   * Signing in is the gate now, not a fallback. Guest sessions meant an invite
   * link worked without an account, but a guest was only ever that browser --
   * clearing cookies or switching device stranded whatever squares they had
   * paid for. The panel stays open because there is nothing behind it.
   */
  if (!session?.user) {
    return (
      <div className="app">
        <header className="banner">
          <h1>{APP_NAME}</h1>
          <div className="banner-meta">{APP_TAGLINE}</div>
        </header>
        <div className="empty-state">
          <p>Sign in to see your board.</p>
          <p className="small">
            We&rsquo;ll email you a six-digit code. No password, no sign-up form.
          </p>
        </div>
        <AuthPanel onClose={() => undefined} />
      </div>
    );
  }

  const user: SessionUser = {
    id: session.user.id,
    name: session.user.name || session.user.email || 'Player',
    email: session.user.email ?? '',
  };

  const gameMatch = /^\/g\/([A-Za-z0-9-]{1,36})$/.exec(route);

  return (
    <div className="app">
      <nav className="row app-nav">
        {/* Only a way back when there is somewhere to go back to. On the list
            itself it navigated to the page already showing, so it looked
            broken. */}
        {gameMatch && (
          <button type="button" className="btn-ghost" onClick={() => navigate('/')}>
            ← All games
          </button>
        )}
        <div className="spacer" />
        <span className="small muted">{user.name}</span>
        <button
          type="button"
          className="btn-ghost"
          onClick={async () => {
            await signOut();
            navigate('/');
          }}
        >
          Sign out
        </button>
      </nav>

      {gameMatch ? (
        <GamePage gameId={gameMatch[1]!} user={user} />
      ) : (
        <GamesLanding user={user} onOpenGame={(id) => navigate(`/g/${id}`)} />
      )}

      {showAuth && <AuthPanel onClose={() => setShowAuth(false)} />}
    </div>
  );
}
